/**
 * Router Compiler
 *
 * Generates route definitions from DomainDefinition resources.
 * Converts URL templates to route patterns and attaches authz rules.
 */

import type { ResourceDefinition } from "../schema/domain-definition";
import type { CompiledAuthzRule, CompiledRoute } from "../runtime/domain-runtime";

/**
 * Convert a URL template to a route pattern.
 * Transforms `{param}` placeholders to `:param` for router matching.
 *
 * @example
 * templateToPattern("/api/bible/{organizationId}/summary")
 * // => "/api/bible/:organizationId/summary"
 */
export function templateToPattern(template: string): string {
  return template.replace(/\{(\w+)\}/g, (_match, name) => `:${name}`);
}

/**
 * Extract parameter names from a URL template.
 *
 * @example
 * extractParams("/api/bible/{organizationId}/summary")
 * // => ["organizationId"]
 */
export function extractParams(template: string): string[] {
  const params: string[] = [];
  const regex = /\{(\w+)\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(template)) !== null) {
    const [, name] = match;
    if (name !== undefined) params.push(name);
  }
  return params;
}

/**
 * Compile route definitions from domain resources.
 *
 * Each resource becomes a route with:
 * - HTTP method from fetch config
 * - URL pattern from template (with :param placeholders)
 * - Type name for response validation
 * - Compiled authz rule
 */
export function compileRoutes(
  resources: Record<string, ResourceDefinition>,
  authzRules: Record<string, CompiledAuthzRule>
): CompiledRoute[] {
  const routes: CompiledRoute[] = [];

  for (const [key, resource] of Object.entries(resources)) {
    const authz = authzRules[key];
    // authzRules is compiled from the same resources map, so every key resolves;
    // the guard satisfies noUncheckedIndexedAccess and is defensive.
    if (!authz) continue;
    routes.push({
      method: resource.fetch.method,
      urlPattern: templateToPattern(resource.fetch.urlTemplate),
      resourceKey: key,
      typeName: resource.type,
      authz,
    });
  }

  return routes;
}
